import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHero } from "../components/PageShell";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { careerValues, futureRoles, EMAIL } from "../data/content";

export default function Careers() {
  const applyFor = (role) => {
    const subject = encodeURIComponent(`Career Profile — ${role} — Spark Link IT Solutions`);
    const body = encodeURIComponent(
      `Hi Amit,\n\nI'm interested in future ${role} opportunities at Spark Link IT Solutions.\n\nName:\nCurrent role:\nExperience:\nPortfolio / LinkedIn:\n\nA short note about me:\n`
    );
    window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
    toast.success("Your email app is opening — your profile is ready to send.");
  };

  return (
    <PageShell>
      <div data-testid="careers-page">
        <PageHero
          overline="Careers"
          title="Build What's Next With Us."
          description="We're building a technology company around talented people who enjoy solving meaningful problems. As Spark Link grows, we are looking to collaborate with engineers, designers, testers, cloud specialists, and technology professionals who care about quality."
        />

        <section className="border-b border-white/[0.07] py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading overline="What we value" title="The Way We Work." />
            <div className="mt-12 flex flex-wrap gap-3" data-testid="careers-values">
              {careerValues.map((value, i) => (
                <Reveal key={value} delay={0.05 * i}>
                  <span className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-6 py-3 font-display text-sm sm:text-base font-medium text-slate-200 transition-colors duration-300 hover:border-cyan-400/50 hover:text-white">
                    <span className="font-mono text-[10px] tracking-[0.2em] text-cyan-400">
                      0{i + 1}
                    </span>
                    {value}
                  </span>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28" data-testid="future-roles-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
              overline="Future opportunities"
              title="Where We're Growing."
              description="We're always interested in meeting talented people across these areas — even when no position is formally open."
            />
            <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
              {futureRoles.map((role, i) => (
                <Reveal key={role.title} delay={0.05 * i} className="h-full">
                  <Link
                    to={`/careers/${role.slug}`}
                    data-testid={`role-card-${i}`}
                    className="group flex h-full w-full flex-col justify-between gap-10 bg-void p-7 text-left transition-colors duration-300 hover:bg-obsidian"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs tracking-[0.2em] text-slate-600">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <ArrowUpRight className="h-4 w-4 text-slate-600 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-cyan-400" />
                    </div>
                    <p className="font-display text-lg font-semibold text-white">{role.title}</p>
                  </Link>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <div className="card-gradient mt-16 flex flex-col items-start justify-between gap-8 rounded-2xl border border-white/10 p-8 lg:flex-row lg:items-center lg:p-12">
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                    Don't See an Open Position?
                  </h2>
                  <p className="mt-3 max-w-lg text-sm sm:text-base text-slate-400">
                    We're always interested in meeting talented people. Send your profile and tell
                    us what you love to build.
                  </p>
                </div>
                <button
                  onClick={() => applyFor("General")}
                  data-testid="send-profile-btn"
                  className="spark-gradient group inline-flex shrink-0 items-center gap-2 rounded-full px-7 py-3.5 font-display text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                  Send Your Profile
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </PageShell>
  );
}
